import { NavLink } from 'react-router-dom'
import { HomeIcon, TimelineIcon, PeopleIcon, AskIcon, NeedIcon } from '@/design-system/icons'
import { useAppStore } from '@/stores/appStore'
import { useUnread, type TabKey } from '@/lib/unreadBadges'

const TABS: Array<{ to: string; label: string; icon: typeof HomeIcon; unreadKey?: TabKey }> = [
  { to: '/', label: 'Today', icon: HomeIcon, unreadKey: 'today' },
  { to: '/needs', label: 'Needs', icon: NeedIcon, unreadKey: 'needs' },
  { to: '/timeline', label: 'Timeline', icon: TimelineIcon, unreadKey: 'timeline' },
  { to: '/family', label: 'Family', icon: PeopleIcon, unreadKey: 'family' },
  { to: '/ask', label: 'Ask', icon: AskIcon },
]

/**
 * TabBar — fixed to viewport bottom (not sticky), so it stays anchored regardless of
 * scroll position or content height. App shell adds bottom padding to main so content
 * doesn't sit under it.
 *
 * Glass-tile backdrop + safe-area padding on the container.
 */
export function TabBar() {
  const openNeeds = useAppStore((s) => s.openNeedsCount)
  const unread = useUnread((s) => s.counts)
  const markSeen = useUnread((s) => s.markSeen)
  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-30 border-t border-divider-subtle backdrop-blur-md"
      style={{
        background: 'rgb(var(--bg-base) / 0.88)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
      aria-label="Primary"
    >
      <div className="mx-auto flex max-w-lg items-stretch justify-around px-2 pt-2 pb-2">
        {TABS.map((t) => {
          const Icon = t.icon
          // For Needs tab, badge shows OPEN needs count. For Timeline/Today/Family,
          // badge shows UNREAD (live-arrived while user was elsewhere) count.
          const badgeCount =
            t.to === '/needs' ? openNeeds :
            t.unreadKey ? unread[t.unreadKey] : 0
          const badgeTone: 'coral' | 'mint' = t.to === '/needs' ? 'coral' : 'mint'
          return (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.to === '/'}
              onClick={() => { if (t.unreadKey) markSeen(t.unreadKey) }}
              className={({ isActive }) =>
                `flex flex-1 flex-col items-center gap-0.5 rounded-r-sm px-2 py-1 min-w-0 relative transition-colors ${isActive ? 'text-accent-coral' : 'text-text-tertiary hover:text-text-secondary'}`
              }
            >
              <div className="relative">
                <Icon size={22} />
                {badgeCount > 0 && (
                  <span
                    className="absolute -top-1 -right-2 min-w-[16px] h-4 rounded-full text-[9px] font-bold flex items-center justify-center px-1 text-white"
                    style={{
                      background: badgeTone === 'coral'
                        ? 'linear-gradient(180deg, #FF8942 0%, #FF7A2D 100%)'
                        : 'linear-gradient(180deg, #40DDAF 0%, #2ED2A3 100%)',
                      boxShadow: badgeTone === 'coral'
                        ? '0 4px 12px -2px rgba(255,122,45,0.55)'
                        : '0 4px 12px -2px rgba(46,210,163,0.55)',
                    }}
                  >
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
              </div>
              <span className="text-[10px] font-medium">{t.label}</span>
            </NavLink>
          )
        })}
      </div>
    </nav>
  )
}
